import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  TrendingUp,
  TrendingDown,
  Calendar,
  Wallet,
  AlertTriangle,
  Target,
  RefreshCw,
} from 'lucide-react';
import { GlassCard, GlassButton, GlassChart, GlassSelect, FadeIn } from '../../components/ui';
import { api } from '../../hooks/useApi';

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });

export const FinanceiroFluxoCaixa = () => {
  const [period, setPeriod] = useState('90');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadFluxo = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/contabilidade/fluxo-caixa', {
        params: { dias: period },
      });
      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao carregar fluxo de caixa.');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    loadFluxo();
  }, [loadFluxo]);

  const projection = data?.projection || [];
  const stats = useMemo(() => ({
    saldoAtual: data?.saldo_atual || 0,
    previsao: data?.previsao_saldo || 0,
    maiorEntrada: data?.maior_entrada || 0,
    maiorSaida: data?.maior_saida || 0,
    mediaDiaria: data?.media_diaria || 0,
    receitasVencidas: data?.receitas_vencidas || 0,
    receitasNoPeriodo: data?.receitas_no_periodo || 0,
    despesasVencidas: data?.despesas_vencidas || 0,
    despesasNoPeriodo: data?.despesas_no_periodo || 0,
  }), [data]);

  const metaPercent = Math.max(0, Math.min(100, (Number(stats.saldoAtual || 0) / 200000) * 100));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-white/90">Fluxo de Caixa</h1>
	          <p className="text-white/50">
	            {loading ? 'Carregando projecao real...' : `Inclui pendencias vencidas e vencimentos ate ${data?.periodo?.fim || '-'}`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <GlassSelect
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            placeholder=""
            className="min-w-[150px]"
            options={[
              { value: '30', label: '30 dias' },
              { value: '60', label: '60 dias' },
              { value: '90', label: '90 dias' },
              { value: '180', label: '180 dias' },
            ]}
          />
          <GlassButton variant="secondary" icon={RefreshCw} onClick={loadFluxo} loading={loading}>
            Atualizar
          </GlassButton>
        </div>
      </div>

      {error && (
        <GlassCard className="p-4 border-red-500/30 bg-red-500/10" hover={false}>
          <p className="text-sm text-red-300">{error}</p>
        </GlassCard>
      )}

      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <GlassCard className="p-4">
	          <p className="text-sm text-white/50">Saldo bancario cadastrado</p>
          <p className="text-xl font-bold text-white/90">{formatCurrency(stats.saldoAtual)}</p>
        </GlassCard>
        <GlassCard className="p-4">
	          <p className="text-sm text-white/50">Previsao do periodo</p>
          <p className={`text-xl font-bold ${stats.previsao >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {formatCurrency(stats.previsao)}
          </p>
        </GlassCard>
        <GlassCard className="p-4">
          <p className="text-sm text-white/50">Maior entrada</p>
          <p className="text-xl font-bold text-accent-primary">{formatCurrency(stats.maiorEntrada)}</p>
        </GlassCard>
        <GlassCard className="p-4">
          <p className="text-sm text-white/50">Maior saida</p>
          <p className="text-xl font-bold text-red-400">{formatCurrency(stats.maiorSaida)}</p>
        </GlassCard>
        <GlassCard className="p-4">
          <p className="text-sm text-white/50">Media diaria</p>
          <p className={`text-xl font-bold ${stats.mediaDiaria >= 0 ? 'text-yellow-400' : 'text-red-400'}`}>
            {formatCurrency(stats.mediaDiaria)}
          </p>
        </GlassCard>
	      </div>

	      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
	        <GlassCard className="p-4 border-red-500/20 bg-red-500/5">
	          <p className="text-sm text-white/50">Receitas vencidas</p>
	          <p className="text-xl font-bold text-red-300">{formatCurrency(stats.receitasVencidas)}</p>
	        </GlassCard>
	        <GlassCard className="p-4 border-green-500/20 bg-green-500/5">
	          <p className="text-sm text-white/50">Receitas a vencer no periodo</p>
	          <p className="text-xl font-bold text-green-400">{formatCurrency(stats.receitasNoPeriodo)}</p>
	        </GlassCard>
	        <GlassCard className="p-4 border-orange-500/20 bg-orange-500/5">
	          <p className="text-sm text-white/50">Despesas vencidas</p>
	          <p className="text-xl font-bold text-orange-300">{formatCurrency(stats.despesasVencidas)}</p>
	        </GlassCard>
	        <GlassCard className="p-4 border-red-500/20 bg-red-500/5">
	          <p className="text-sm text-white/50">Despesas a vencer no periodo</p>
	          <p className="text-xl font-bold text-red-400">{formatCurrency(stats.despesasNoPeriodo)}</p>
	        </GlassCard>
	      </div>

      <FadeIn>
        <GlassCard>
          <div className="p-6 border-b border-white/10">
            <h3 className="text-lg font-semibold text-white/90">Projecao de Saldo</h3>
	            <p className="text-sm text-white/50">Saldo bancario cadastrado + pendencias vencidas + pendencias dentro do periodo selecionado</p>
          </div>
          <div className="p-6">
            <GlassChart
              type="area"
              data={projection}
              dataKeys={[{ key: 'saldo', name: 'Saldo projetado' }]}
              xAxisKey="day"
              height={400}
              colors={['#6366f1']}
            />
          </div>
        </GlassCard>
      </FadeIn>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <FadeIn delay={0.2}>
          <GlassCard>
            <div className="p-6 border-b border-white/10">
              <h3 className="text-lg font-semibold text-white/90">Entradas vs Saidas</h3>
            </div>
            <div className="p-6">
              <GlassChart
                type="bar"
                data={projection}
                dataKeys={[
                  { key: 'receitas', name: 'Receitas' },
                  { key: 'despesas', name: 'Despesas' },
                ]}
                xAxisKey="day"
                height={250}
                colors={['#22c55e', '#ef4444']}
              />
            </div>
          </GlassCard>
        </FadeIn>

        <FadeIn delay={0.3}>
          <GlassCard>
            <div className="p-6 border-b border-white/10">
              <h3 className="text-lg font-semibold text-white/90 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-yellow-400" />
                Alertas de Fluxo
              </h3>
            </div>
            <div className="p-6 space-y-4">
              {(data?.alertas || []).length > 0 ? (
                data.alertas.map((alert, index) => (
                  <div
                    key={`${alert.message}-${index}`}
                    className={`p-4 rounded-xl border ${
                      alert.type === 'danger'
                        ? 'bg-red-500/10 border-red-500/30'
                        : 'bg-yellow-500/10 border-yellow-500/30'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <span className={`text-xs font-medium px-2 py-1 rounded ${
                        alert.type === 'danger' ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {alert.day}
                      </span>
                      <p className="text-sm text-white/80">{alert.message}</p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/20">
                  <div className="flex items-center gap-2 mb-2">
                    <Target className="w-5 h-5 text-green-400" />
                    <span className="font-medium text-green-400">Sem alertas no periodo</span>
                  </div>
                  <p className="text-sm text-white/70">Nao ha despesas vencidas ou saldo negativo projetado.</p>
                </div>
              )}

              <div className="mt-6 p-4 rounded-xl bg-white/[0.03] border border-white/10">
                <div className="flex items-center gap-2 mb-2">
                  <Wallet className="w-5 h-5 text-accent-primary" />
                  <span className="font-medium text-white/90">Referencia de caixa</span>
                </div>
                <p className="text-sm text-white/70">Meta operacional: {formatCurrency(200000)}</p>
                <div className="mt-3 w-full h-2 bg-white/10 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-green-500 to-accent-primary rounded-full"
                    style={{ width: `${metaPercent}%` }}
                  />
                </div>
              </div>
            </div>
          </GlassCard>
        </FadeIn>
      </div>
    </div>
  );
};

export default FinanceiroFluxoCaixa;
